import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import RegionBlocked from "@/components/RegionBlocked";
import { LockBadge } from "@/components/LockedContent";
import { useSeo } from "@/lib/useSeo";
import { renderEmphasis } from "@/lib/richText";

export default function Journal() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState({});
  const [blocked, setBlocked] = useState(false);

  useSeo({
    title: "Journal",
    description: "Essays, letters and reflections by Gemma Veda — notes from a writing life kept by the sea.",
  });

  useEffect(() => {
    api.get("/posts")
      .then(({ data }) => setPosts(data))
      .catch((err) => { if (err.response?.status === 451) setBlocked(true); })
      .finally(() => setLoading(false));
    api.get("/settings").then(({ data }) => setContent(data)).catch(() => {});
  }, []);

  // The "most-read" slug — only awarded if the top post has more views than everyone else and views > 0.
  const mostReadSlug = useMemo(() => {
    if (!posts.length) return null;
    const sorted = [...posts].sort((a, b) => (b.views || 0) - (a.views || 0));
    const top = sorted[0];
    if (!top || (top.views || 0) === 0) return null;
    // If there's a tie for #1, don't crown any one.
    if (sorted.length > 1 && (sorted[1].views || 0) === (top.views || 0)) return null;
    return top.slug;
  }, [posts]);

  if (blocked) return <RegionBlocked />;

  return (
    <PublicLayout>
      <section data-testid="journal-page" className="pt-36 pb-20 px-6 md:px-12 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="gv-label">Journal</div>
            <h1 className="font-serif text-5xl md:text-7xl mt-4 text-[#1A1F24] leading-[1.05] max-w-3xl" data-testid="journal-heading">
              {renderEmphasis(content.journal_heading || "Notes on the *journey*.")}
            </h1>
            <p className="mt-6 text-[#5C6670] text-lg max-w-2xl font-light leading-relaxed" data-testid="journal-subtext">
              {content.journal_subtext || "Reflections, letters and short essays — the unfolding diary behind the Soul's CODE series."}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="pb-32 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          {loading && <div className="text-center text-[#5C6670] py-20">Loading…</div>}
          {!loading && posts.length === 0 && (
            <div className="text-center py-20">
              <p className="font-serif text-2xl italic text-[#5C6670]">No entries yet. The pen is warming up.</p>
            </div>
          )}
          <div className="divide-y divide-[#E5E5E5]">
            {posts.map((p, i) => (
              <motion.article
                key={p.id}
                data-testid={`journal-card-${p.slug}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                className="py-12 md:py-16 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center"
              >
                <Link to={`/journal/${p.slug}`} className="md:col-span-5 group block relative">
                  {mostReadSlug === p.slug && (
                    <div
                      className="absolute top-4 left-4 z-10 bg-[#C5A059] text-white text-[0.65rem] tracking-[0.32em] uppercase px-3 py-1.5 shadow-lg font-medium"
                      data-testid={`journal-most-read-${p.slug}`}
                    >
                      Most read
                    </div>
                  )}
                  {p.locked && <LockBadge className="absolute top-4 right-4 z-10 shadow-lg" testId={`journal-lock-${p.slug}`} />}
                  <div className="aspect-[4/3] overflow-hidden bg-[#E5E5E5]">
                    {p.cover_image && <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}
                  </div>
                </Link>
                <div className="md:col-span-7">
                  <div className="flex items-center gap-3 text-xs tracking-[0.22em] uppercase text-[#5C6670]">
                    <span className="text-[#C5A059]">{p.tag || "Essay"}</span>
                    <span className="w-6 h-px bg-[#E5E5E5]" />
                    <span>{new Date(p.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}</span>
                    <span className="w-6 h-px bg-[#E5E5E5]" />
                    <span data-testid={`journal-views-${p.slug}`} className="text-[#B8BCC2]">
                      {(p.views || 0).toLocaleString()} {p.views === 1 ? "read" : "reads"}
                    </span>
                  </div>
                  <h2 className="font-serif text-3xl md:text-4xl mt-4 text-[#1A1F24] leading-tight">
                    <Link to={`/journal/${p.slug}`} className="hover:text-[#A68545] transition-colors">{p.title}</Link>
                  </h2>
                  <p className="mt-4 text-[#5C6670] text-base md:text-lg font-light leading-relaxed">{p.excerpt}</p>
                  <Link to={`/journal/${p.slug}`} className="inline-block mt-6 text-sm tracking-[0.2em] uppercase gv-link text-[#C5A059]" data-testid={`journal-read-${p.slug}`}>
                    Read the entry →
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
